import { useEffect, useState } from 'react';
import cliSpinners, { Spinner } from 'cli-spinners';

interface UseCliSpinnerOptions {
  /** The spinner to use. Defaults to cliSpinners.dots */
  spinner?: Spinner;
  /** Whether the spinner animation is active. Defaults to true */
  isActive?: boolean;
}

/**
 * Hook to animate cli-spinners in React components.
 * Handles the interval logic internally and returns the current frame.
 *
 * @example
 * const frame = useCliSpinner(); // Always animating
 * const frame = useCliSpinner({ isActive: isLoading }); // Only when loading
 * const frame = useCliSpinner({ spinner: cliSpinners.line }); // Different spinner
 */
export function useCliSpinner(options: UseCliSpinnerOptions = {}): string {
  const { spinner = cliSpinners.dots, isActive = true } = options;

  // Start at 0 for consistent server/client hydration
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    // Sync to current time on mount, then continue interval
    setFrameIndex(Math.floor(Date.now() / spinner.interval) % spinner.frames.length);

    const interval = setInterval(() => {
      setFrameIndex(Math.floor(Date.now() / spinner.interval) % spinner.frames.length);
    }, spinner.interval);

    return () => clearInterval(interval);
  }, [isActive, spinner.interval, spinner.frames.length]);

  return spinner.frames[frameIndex];
}
